import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Colores semánticos (alertas, acentos funcionales)
const ROJO     = '#e41b23';

// Paleta de impresión (alineada con HojaFrontal.js)
const GRIS_OSCURO = '#111111';
const GRIS        = '#555555';
const GRIS_CLARO  = '#f8f9fa';
const GRIS_MED    = '#e0e0e0';
const BORDE       = '#cccccc';
const BLANCO      = '#ffffff';
const TEXTO       = '#111111';

const estilos = StyleSheet.create({
    /* ── Portada ───────────────────────────────── */
    pagina: {
        padding: 0,
        fontFamily: 'Helvetica',
        backgroundColor: BLANCO,
    },
    cabecera: {
        backgroundColor: GRIS_CLARO,
        borderBottomWidth: 1,
        borderBottomColor: BORDE,
        paddingTop: 28,
        paddingBottom: 22,
        paddingLeft: 40,
        paddingRight: 40,
    },
    cabeceraLabel: {
        fontSize: 8,
        color: GRIS,
        letterSpacing: 2,
        marginBottom: 6,
    },
    cabeceraTitulo: {
        fontSize: 22,
        color: GRIS_OSCURO,
        fontFamily: 'Helvetica-Bold',
    },
    accentBar: {
        height: 2,
        backgroundColor: GRIS_MED,
    },
    contenido: {
        paddingTop: 28,
        paddingBottom: 48,
        paddingLeft: 40,
        paddingRight: 40,
    },
    folioFila: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 18,
    },
    folioPastilla: {
        backgroundColor: BLANCO,
        borderWidth: 1,
        borderColor: BORDE,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 3,
    },
    folioTexto: {
        fontSize: 9,
        color: GRIS_OSCURO,
        fontFamily: 'Helvetica-Bold',
        letterSpacing: 1,
    },
    seccion: {
        marginBottom: 16,
        backgroundColor: GRIS_CLARO,
        borderWidth: 0.5,
        borderColor: BORDE,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 12,
    },
    seccionTitulo: {
        fontSize: 7,
        color: GRIS,
        fontFamily: 'Helvetica-Bold',
        letterSpacing: 1.5,
        marginBottom: 8,
    },
    filaInfo: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    etiqueta: {
        fontSize: 8,
        color: GRIS,
        width: 140,
    },
    valor: {
        fontSize: 8,
        color: TEXTO,
        fontFamily: 'Helvetica-Bold',
        flex: 1,
    },
    divisor: {
        height: 1,
        backgroundColor: BORDE,
        marginTop: 12,
        marginBottom: 16,
    },
    alertaBanda: {
        backgroundColor: '#fff5f5',
        borderWidth: 1,
        borderColor: '#fca5a5',
        borderLeftWidth: 4,
        borderLeftColor: ROJO,
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        marginBottom: 8,
    },
    alertaTitulo: {
        fontSize: 11,
        color: ROJO,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
    },
    alertaTexto: {
        fontSize: 9,
        color: '#7f1d1d',
        lineHeight: 1.6,
    },
    alertaEfectivoBanda: {
        backgroundColor: '#fffbeb',
        borderWidth: 1,
        borderColor: '#fcd34d',
        borderLeftWidth: 4,
        borderLeftColor: '#d97706',
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        marginBottom: 8,
    },
    alertaEfectivoTitulo: {
        fontSize: 11,
        color: '#92400e',
        fontFamily: 'Helvetica-Bold',
        marginBottom: 4,
    },
    alertaEfectivoTexto: {
        fontSize: 9,
        color: '#78350f',
        lineHeight: 1.6,
    },
    alertaEfectivoMonto: {
        fontSize: 13,
        color: '#92400e',
        fontFamily: 'Helvetica-Bold',
        marginTop: 4,
    },
    docEstado: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        flex: 1,
    },
    docEstadoSi: {
        color: '#166534',
    },
    docEstadoNo: {
        color: '#991b1b',
    },
    nota: {
        position: 'absolute',
        bottom: 28,
        left: 40,
        right: 40,
        borderTopWidth: 1,
        borderTopColor: BORDE,
        paddingTop: 8,
    },
    notaTexto: {
        fontSize: 7.5,
        color: '#999999',
        textAlign: 'center',
    },
    /* ── Separador Sujeto / Operación ─────────── */
    separadorBanda: {
        backgroundColor: GRIS_CLARO,
        borderBottomWidth: 1,
        borderBottomColor: BORDE,
        paddingTop: 48,
        paddingBottom: 40,
        paddingLeft: 40,
        paddingRight: 40,
    },
    separadorEtiqueta: {
        fontSize: 8,
        color: GRIS,
        letterSpacing: 2.5,
        marginBottom: 10,
    },
    separadorNombre: {
        fontSize: 26,
        color: GRIS_OSCURO,
        fontFamily: 'Helvetica-Bold',
        lineHeight: 1.3,
    },
    separadorCuerpo: {
        paddingTop: 28,
        paddingBottom: 40,
        paddingLeft: 40,
        paddingRight: 40,
    },
    separadorRfcCaja: {
        backgroundColor: BLANCO,
        borderWidth: 1,
        borderColor: BORDE,
        borderLeftWidth: 3,
        borderLeftColor: GRIS,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 12,
        marginBottom: 20,
        alignSelf: 'flex-start',
    },
    separadorRfcLabel: {
        fontSize: 7,
        color: GRIS,
        fontFamily: 'Helvetica-Bold',
        letterSpacing: 1.2,
        marginBottom: 3,
    },
    separadorRfcValor: {
        fontSize: 14,
        color: TEXTO,
        fontFamily: 'Helvetica-Bold',
        letterSpacing: 1,
    },
    separadorTexto: {
        fontSize: 9,
        color: GRIS,
        lineHeight: 1.5,
    },
});

function formatPesos(monto) {
    const entero = Math.floor(monto);
    const decimales = Math.round((monto - entero) * 100).toString().padStart(2, '0');
    const milesStr = entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$ ' + milesStr + '.' + decimales + ' MXN';
}

/**
 * Retorna el monto total de comprobantes_pago cuya forma_pago sea EFECTIVO.
 * Si no hay pagos en efectivo retorna 0.
 */
function calcularEfectivo(comprobantes) {
    if (!Array.isArray(comprobantes) || comprobantes.length === 0) return 0;
    return comprobantes
        .filter(c => typeof c.forma_pago === 'string' && c.forma_pago.toUpperCase().includes('EFECTIVO'))
        .reduce((acc, c) => {
            const raw = String(c.forma_pago_monto || '0').replace(/[^0-9.]/g, '');
            return acc + (parseFloat(raw) || 0);
        }, 0);
}

function folioExpediente(operacion) {
    const num = operacion && operacion.num_expediente;
    if (num != null) {
        return `EXP-${String(num).padStart(4, '0')}`;
    }
    // Fallback para registros anteriores sin num_expediente
    const id = operacion && (operacion._id || operacion.id);
    const s = String(id || '');
    return `EXP-${s.replace(/[^a-fA-F0-9]/g, '').slice(0, 8).toUpperCase()}`;
}

function nombreSujeto(s) {
    if (!s) return '—';
    if (s.razon_social) return String(s.razon_social);
    if (s.nombre_completo) return String(s.nombre_completo);
    const n = [s.nombre, s.apellido_paterno, s.apellido_materno].filter(Boolean).join(' ');
    return n || String(s.rfc || 'Sujeto');
}

/** Portada del expediente maestro para revisión / auditoría documental. */
export function PortadaAuditoria({
    operacion,
    sujetos,
    sujetosFaltantes = 0,
    textoFaltantes = '',
}) {
    const lista = Array.isArray(sujetos) ? sujetos : [];

    // ── Documentos de la operación ──────────────────────────────────────────
    const contratoInter   = operacion && operacion.contrato_intermediacion;
    const contratoOp      = operacion && operacion.contrato_operacion;
    const comprobantes    = (operacion && operacion.comprobantes_pago) || [];
    const tieneContratoI  = !!(contratoInter && contratoInter.url);
    const tieneContratoO  = !!(contratoOp && contratoOp.url);
    const numComprobantes = comprobantes.length;

    // ── Pagos en efectivo ───────────────────────────────────────────────────
    const montoEfectivo   = calcularEfectivo(comprobantes);
    return (
        <Document>
            <Page size="A4" style={estilos.pagina}>
                {/* Cabecera institucional */}
                <View style={estilos.cabecera}>
                    <Text style={estilos.cabeceraLabel}>PREVENCIÓN DE LAVADO DE DINERO</Text>
                    <Text style={estilos.cabeceraTitulo}>Expediente PLD</Text>
                </View>
                <View style={estilos.accentBar} />

                <View style={estilos.contenido}>
                    {/* Folio */}
                    <View style={estilos.folioFila}>
                        <View style={estilos.folioPastilla}>
                            <Text style={estilos.folioTexto}>{folioExpediente(operacion)}</Text>
                        </View>
                    </View>

                    {/* Datos de la operación */}
                    <View style={estilos.seccion}>
                        <Text style={estilos.seccionTitulo}>DATOS DE LA OPERACIÓN</Text>
                        <View style={estilos.filaInfo}>
                            <Text style={estilos.etiqueta}>
                                {operacion && operacion.tipo_operacion === 'INMOBILIARIO'
                                    ? 'Operación'
                                    : 'Denominación'}
                            </Text>
                            <Text style={estilos.valor}>
                                {operacion && operacion.tipo_operacion === 'INMOBILIARIO'
                                    ? (operacion.asunto || '—')
                                    : (operacion.denominacion || '—')}
                            </Text>
                        </View>
                        <View style={estilos.filaInfo}>
                            <Text style={estilos.etiqueta}>Tipo de operación</Text>
                            <Text style={estilos.valor}>
                                {(operacion && operacion.tipo_operacion) || '—'}
                            </Text>
                        </View>
                        <View style={estilos.filaInfo}>
                            <Text style={estilos.etiqueta}>Sujetos en expediente</Text>
                            <Text style={estilos.valor}>{lista.length}</Text>
                        </View>
                    </View>

                    {/* Documentos de la operación */}
                    <View style={estilos.seccion}>
                        <Text style={estilos.seccionTitulo}>DOCUMENTOS DE LA OPERACIÓN</Text>
                        <View style={estilos.filaInfo}>
                            <Text style={estilos.etiqueta}>Contrato de intermediación</Text>
                            <Text style={[estilos.docEstado, tieneContratoI ? estilos.docEstadoSi : estilos.docEstadoNo]}>
                                {tieneContratoI
                                    ? `Adjunto${contratoInter.fecha ? '  ·  ' + contratoInter.fecha : ''}`
                                    : 'Pendiente'}
                            </Text>
                        </View>
                        <View style={estilos.filaInfo}>
                            <Text style={estilos.etiqueta}>Contrato de operación</Text>
                            <Text style={[estilos.docEstado, tieneContratoO ? estilos.docEstadoSi : estilos.docEstadoNo]}>
                                {tieneContratoO
                                    ? `Adjunto${contratoOp.fecha ? '  ·  ' + contratoOp.fecha : ''}`
                                    : 'Pendiente'}
                            </Text>
                        </View>
                        <View style={estilos.filaInfo}>
                            <Text style={estilos.etiqueta}>Comprobantes de pago</Text>
                            <Text style={estilos.valor}>
                                {numComprobantes > 0
                                    ? `${numComprobantes} comprobante(s) adjunto(s)`
                                    : 'Sin comprobantes'}
                            </Text>
                        </View>
                    </View>

                    <View style={estilos.divisor} />

                    {/* Alerta de pago en efectivo */}
                    {montoEfectivo > 0 ? (
                        <View style={estilos.alertaEfectivoBanda}>
                            <Text style={estilos.alertaEfectivoTitulo}>
                                ⚠  Operación con pago(s) en efectivo
                            </Text>
                            <Text style={estilos.alertaEfectivoTexto}>
                                Se identificaron comprobantes de pago con forma de pago en efectivo.
                                De conformidad con la normativa de PLD, los pagos en efectivo requieren
                                atención especial y verificación de origen de recursos.
                            </Text>
                            <Text style={estilos.alertaEfectivoMonto}>
                                Monto total en efectivo: {formatPesos(montoEfectivo)}
                            </Text>
                        </View>
                    ) : null}

                    {/* Alerta de faltantes */}
                    {sujetosFaltantes > 0 ? (
                        <View style={estilos.alertaBanda}>
                            <Text style={estilos.alertaTitulo}>
                                {sujetosFaltantes} sujeto(s) con documentación pendiente
                            </Text>
                            <Text style={estilos.alertaTexto}>{textoFaltantes}</Text>
                        </View>
                    ) : null}
                </View>

                {/* Nota al pie */}
                <View style={estilos.nota}>
                    <Text style={estilos.notaTexto}>
                        Documento generado para revisión documental y archivo del expediente. Uso interno.
                    </Text>
                </View>
            </Page>
        </Document>
    );
}

/** Página separadora antes de los documentos de la operación. */
export function SeparadorOperacion({ operacion }) {
    const folio = folioExpediente(operacion);
    const denominacion = operacion
        ? (operacion.tipo_operacion === 'INMOBILIARIO'
            ? operacion.asunto
            : operacion.denominacion) || '—'
        : '—';
    return (
        <Document>
            <Page size="A4" style={estilos.pagina}>
                <View style={estilos.separadorBanda}>
                    <Text style={estilos.separadorEtiqueta}>DOCUMENTOS DE LA OPERACIÓN</Text>
                    <Text style={estilos.separadorNombre}>{denominacion}</Text>
                </View>
                <View style={estilos.accentBar} />

                <View style={estilos.separadorCuerpo}>
                    <View style={estilos.separadorRfcCaja}>
                        <Text style={estilos.separadorRfcLabel}>FOLIO</Text>
                        <Text style={estilos.separadorRfcValor}>{folio}</Text>
                    </View>
                    <Text style={estilos.separadorTexto}>
                        Los contratos y comprobantes de pago de esta operación se presentan a continuación.
                    </Text>
                </View>

                <View style={estilos.nota}>
                    <Text style={estilos.notaTexto}>
                        Documento generado para revisión documental y archivo del expediente. Uso interno.
                    </Text>
                </View>
            </Page>
        </Document>
    );
}

/** Página separadora antes de los anexos de cada sujeto. */
export function SeparadorSujeto({ sujeto }) {
    const titulo = nombreSujeto(sujeto);
    const rfc = sujeto && sujeto.rfc ? String(sujeto.rfc) : null;
    return (
        <Document>
            <Page size="A4" style={estilos.pagina}>
                {/* Banda roja con nombre del sujeto */}
                <View style={estilos.separadorBanda}>
                    <Text style={estilos.separadorEtiqueta}>SUJETO OBLIGADO</Text>
                    <Text style={estilos.separadorNombre}>{titulo}</Text>
                </View>
                <View style={estilos.accentBar} />

                <View style={estilos.separadorCuerpo}>
                    {/* RFC */}
                    {rfc ? (
                        <View style={estilos.separadorRfcCaja}>
                            <Text style={estilos.separadorRfcLabel}>RFC</Text>
                            <Text style={estilos.separadorRfcValor}>{rfc}</Text>
                        </View>
                    ) : null}

                    <Text style={estilos.separadorTexto}>
                        Los anexos y documentos de identificación de este sujeto se presentan a continuación.
                    </Text>
                </View>

                <View style={estilos.nota}>
                    <Text style={estilos.notaTexto}>
                        Documento generado para revisión documental y archivo del expediente. Uso interno.
                    </Text>
                </View>
            </Page>
        </Document>
    );
}
